/**
 * Mengen lesen, umrechnen und schön darstellen.
 *
 * Mengen werden im Rezept als Text gespeichert ("200", "0,5", "1 1/2", "2-3",
 * "ca. 100"). Hier werden sie zu Zahlen, mit einer Umrechnungsart skaliert
 * und wieder so geschrieben, wie man es in einer Küche erwartet: Gramm
 * sinnvoll gerundet, Löffel als Brüche, ab 1000 g in Kilogramm.
 */
import type { ScalingMode } from './types.ts';
import { normalizeUnit } from './units.ts';

/** Eine gelesene Menge. */
export interface Quantity {
  readonly value: number;
  /** Obergrenze bei Spannen wie "2–3". */
  readonly max?: number;
  /** Ungefähre Angabe ("ca. 200"). */
  readonly approx?: boolean;
}

/** Unicode-Brüche und ihr Wert. */
const UNICODE_FRACTIONS: Readonly<Record<string, number>> = {
  '½': 1 / 2,
  '¼': 1 / 4,
  '¾': 3 / 4,
  '⅓': 1 / 3,
  '⅔': 2 / 3,
  '⅛': 1 / 8,
  '⅜': 3 / 8,
  '⅝': 5 / 8,
  '⅞': 7 / 8,
};

/** Liest eine einzelne Zahl: "2", "0,5", "1.5", "1/2", "1 1/2", "1½", "½". */
function parseNumber(text: string): number | null {
  const cleaned = text.trim();
  if (cleaned === '') {
    return null;
  }
  const unicode = /^(\d+)?\s*([½¼¾⅓⅔⅛⅜⅝⅞])$/.exec(cleaned);
  if (unicode !== null) {
    return Number(unicode[1] ?? 0) + (UNICODE_FRACTIONS[unicode[2] ?? ''] ?? 0);
  }
  const mixed = /^(\d+)\s+(\d+)\s*\/\s*(\d+)$/.exec(cleaned);
  if (mixed !== null) {
    const denominator = Number(mixed[3]);
    return denominator === 0 ? null : Number(mixed[1]) + Number(mixed[2]) / denominator;
  }
  const fraction = /^(\d+)\s*\/\s*(\d+)$/.exec(cleaned);
  if (fraction !== null) {
    const denominator = Number(fraction[2]);
    return denominator === 0 ? null : Number(fraction[1]) / denominator;
  }
  if (/^\d+([.,]\d+)?$/.test(cleaned)) {
    return Number(cleaned.replace(',', '.'));
  }
  return null;
}

/**
 * Liest eine Mengenangabe. Liefert null für Angaben ohne Zahl wie "etwas"
 * oder "nach Geschmack"; diese bleiben beim Umrechnen unverändert.
 */
export function parseQuantity(text: string): Quantity | null {
  let rest = text.trim();
  let approx = false;
  const prefix = /^(ca\.?|circa|etwa|ungefähr)\s*/i.exec(rest);
  if (prefix !== null) {
    approx = true;
    rest = rest.slice(prefix[0].length);
  }
  const range = /^(.+?)\s*(?:-|–|bis)\s*(.+)$/.exec(rest);
  if (range !== null) {
    const low = parseNumber(range[1] ?? '');
    const high = parseNumber(range[2] ?? '');
    if (low !== null && high !== null) {
      return approx ? { value: low, max: high, approx } : { value: low, max: high };
    }
  }
  const value = parseNumber(rest);
  if (value === null) {
    return null;
  }
  return approx ? { value, approx } : { value };
}

/** Exponent für unterproportionales Wachsen (Gewürze, Backtriebmittel). */
export const SUBLINEAR_EXPONENT = 0.7;

/**
 * Skaliert eine Zahl nach der Umrechnungsart.
 * - linear: wächst genau mit
 * - rounded: wächst mit und wird auf ganze Stücke gerundet (mindestens eins)
 * - sublinear: wächst langsamer, damit es nicht versalzen wird
 * - fixed: bleibt gleich
 */
export function scaleValue(value: number, factor: number, mode: ScalingMode): number {
  switch (mode) {
    case 'fixed':
      return value;
    case 'sublinear':
      return value * factor ** SUBLINEAR_EXPONENT;
    case 'rounded': {
      const raw = value * factor;
      // Halbe oder viertel Stücke nur, wenn schon das Original kleiner als eins war.
      const step = value < 1 ? (value <= 0.25 ? 0.25 : 0.5) : 1;
      return Math.max(step, Math.round(raw / step) * step);
    }
    default:
      return value * factor;
  }
}

/** Skaliert eine ganze Menge einschließlich Spanne. */
export function scaleQuantity(quantity: Quantity, factor: number, mode: ScalingMode): Quantity {
  const value = scaleValue(quantity.value, factor, mode);
  const max = quantity.max === undefined ? undefined : scaleValue(quantity.max, factor, mode);
  return {
    value,
    ...(max === undefined ? {} : { max }),
    ...(quantity.approx === true ? { approx: true } : {}),
  };
}

/** Metrische Einheiten, die in Zahlen statt Brüchen geschrieben werden. */
const METRIC_UNITS = new Set(['g', 'kg', 'mg', 'ml', 'l', 'cl', 'dl']);

/**
 * Packungen und Gebinde lassen sich höchstens halbieren: „1 ⅛ Päckchen“ wäre
 * unsinnig. Diese Einheiten werden auf halbe Stück gerundet.
 */
const PACKAGE_UNITS = new Set([
  'päckchen',
  'packung',
  'dose',
  'dosen',
  'becher',
  'glas',
  'flasche',
  'würfel',
  'beutel',
  'tüte',
]);

/** Rundet Gramm und Milliliter auf eine Genauigkeit, die man abwiegen kann. */
export function roundMetric(value: number): number {
  const step = value < 1 ? 0.1 : value < 10 ? 0.5 : value < 100 ? 1 : value < 500 ? 5 : 10;
  return Math.round(value / step) * step;
}

/** Zahl in deutscher Schreibweise mit höchstens zwei Nachkommastellen. */
export function formatDecimal(value: number, digits = 2): string {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: digits }).format(value);
}

/** Brüche, die in Rezepten üblich sind. */
const FRACTION_SIGNS: readonly (readonly [number, string])[] = [
  [0, ''],
  [1 / 8, '⅛'],
  [1 / 4, '¼'],
  [1 / 3, '⅓'],
  [1 / 2, '½'],
  [2 / 3, '⅔'],
  [3 / 4, '¾'],
  [1, ''],
];

/**
 * Schreibt eine Zahl als Bruch, wenn sie nahe an einem üblichen Bruch liegt
 * ("1 ½", "¾"), sonst mit einer Nachkommastelle.
 */
export function formatFraction(value: number): string {
  const whole = Math.floor(value);
  const part = value - whole;
  let best: readonly [number, string] = [0, ''];
  for (const candidate of FRACTION_SIGNS) {
    if (Math.abs(candidate[0] - part) < Math.abs(best[0] - part)) {
      best = candidate;
    }
  }
  // Für große Zahlen reicht eine grobe Rundung.
  const tolerance = value >= 10 ? 0.25 : 0.06;
  if (Math.abs(best[0] - part) > tolerance) {
    return formatDecimal(value, 1);
  }
  if (best[0] === 1) {
    return String(whole + 1);
  }
  if (best[1] === '') {
    // Sehr kleine Mengen nicht zu null runden.
    return whole === 0 && value > 0 ? formatDecimal(value, 2) : String(whole);
  }
  return whole === 0 ? best[1] : `${whole} ${best[1]}`;
}

/** Ergebnis der Darstellung: Menge und Einheit getrennt, damit die Oberfläche sie setzen kann. */
export interface DisplayAmount {
  readonly amount: string;
  readonly unit: string;
}

/** Wandelt große Gramm- und Milliliterangaben in Kilogramm und Liter um und umgekehrt. */
function preferredMetric(value: number, unit: string): { value: number; unit: string } {
  const lower = unit.toLowerCase();
  if ((lower === 'g' || lower === 'ml') && value >= 1000) {
    return { value: value / 1000, unit: lower === 'g' ? 'kg' : 'l' };
  }
  if ((lower === 'kg' || lower === 'l') && value < 1) {
    return { value: value * 1000, unit: lower === 'kg' ? 'g' : 'ml' };
  }
  return { value, unit };
}

/** Schreibt eine Zahl in der für die Einheit üblichen Form. */
function formatValue(value: number, unit: string): string {
  const lower = unit.toLowerCase();
  if (lower === 'kg' || lower === 'l') {
    return formatDecimal(Math.round(value * 100) / 100);
  }
  if (METRIC_UNITS.has(lower)) {
    return formatDecimal(roundMetric(value), 1);
  }
  if (PACKAGE_UNITS.has(lower)) {
    return formatFraction(Math.max(0.5, Math.round(value * 2) / 2));
  }
  return formatFraction(value);
}

/** Umrechnungsfaktor zwischen Gramm/Kilogramm bzw. Milliliter/Liter. */
function metricStep(from: string, to: string): number {
  const big = (unit: string) => unit === 'kg' || unit === 'l';
  if (from === to) {
    return 1;
  }
  return big(to) && !big(from) ? 1 / 1000 : big(from) && !big(to) ? 1000 : 1;
}

/**
 * Stellt eine (skalierte) Menge mit Einheit dar. Bei Spannen bestimmt der
 * obere Wert die Einheit, damit nicht "800 g–1,2 kg" entsteht.
 */
export function formatQuantity(quantity: Quantity, unit: string): DisplayAmount {
  const normalized = normalizeUnit(unit);
  const shown = preferredMetric(quantity.max ?? quantity.value, normalized).unit;
  const step = metricStep(normalized.toLowerCase(), shown.toLowerCase());
  const low = formatValue(quantity.value * step, shown);
  const amount = quantity.max === undefined ? low : `${low}–${formatValue(quantity.max * step, shown)}`;
  return { amount: quantity.approx === true ? `ca. ${amount}` : amount, unit: shown };
}
